import React, {useState} from 'react';

import {SignoffFooterPanel} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/SignoffFooterPanel';
import {getCommonDocument} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/utils';
import {BaseActionableDocumentType} from 'app/general/types';
import {useRequestAdhocSignoff} from 'app/signoffs/api';
import {SelectUsersMulti} from 'app/user/components/select/SelectUsers';
import {ShareableContentType} from 'app/user/types';
import {gt} from 'app/utils/i18n';

import type {BasicUserSerializedType} from 'sweet-types';

type Props = {
  contentType: 'procedure' | 'policy' | 'proceduregroup';
  document: BaseActionableDocumentType;
  onClose: () => void;
  onSuccess: (doc?: BaseActionableDocumentType) => unknown;
};

export function SignoffAdhoc({
  document,
  contentType,
  onClose,
  onSuccess,
}: Props) {
  const [status, setStatus] = useState<'start' | 'success' | 'error'>('start');
  const [selectedUsers, setSelectedUsers] = useState<
    Array<BasicUserSerializedType>
  >([]);
  const [requestAdhocSignoff] = useRequestAdhocSignoff();

  const commonDocument = getCommonDocument(document);

  const generateSignoff = (e: React.MouseEvent) => {
    e.preventDefault();
    if (selectedUsers.length === 0 || !document.can_edit) {
      return;
    }
    return requestAdhocSignoff({
      url: commonDocument.current_version.url,
      users: selectedUsers.map((u) => u.url),
      contentType,
    })
      .unwrap()
      .then(() => {
        onSuccess(document);
        onClose();
      })
      .catch((err) => {
        setStatus('error');
        return err;
      });
  };

  return (
    <>
      <h6 className="font-weight-bold text-uppercase text-secondary small mb-3">
        {gt('Require Sign Off for This Version')}
      </h6>
      {status === 'start' ? (
        <div className="mb-3">
          <SelectUsersMulti
            selected={selectedUsers}
            refetchDataOnOpen={true}
            params={{
              content_type: contentType as ShareableContentType,
              object_id: commonDocument.id,
              with_permissions: '1',
            }}
            onChange={(users) => {
              setSelectedUsers(users as Array<BasicUserSerializedType>);
              return Promise.resolve();
            }}
          />
          <small className="text-secondary mt-2 d-block">
            {gt(
              'Only users who already have access to this document are shown.'
            )}
          </small>
        </div>
      ) : status === 'error' ? (
        <div className="alert alert-danger my-1">
          {gt('Request failed. Sign Offs were not requested.')}
        </div>
      ) : null}
      <SignoffFooterPanel
        status={status}
        onClose={onClose}
        disabled={!document.can_edit || selectedUsers.length === 0}
        disabledTooltip={gt('Select at least one user.')}
        onSubmit={generateSignoff}
      />
    </>
  );
}
